import { useEffect, useState } from "react";
import { BookOpen } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { RaceTypeBadge } from "@/components/race/RaceTypeBadge";

const FixedRow = ({ r, i }) => (
  <tr className="border-t border-slate-800 align-top" data-testid={`class-rule-${i}`}>
    <td className="py-3 pr-3 num text-slate-500">{i + 1}</td>
    <td className="py-3 pr-4">
      <div className="text-white font-medium flex items-center gap-2 flex-wrap">{r.rule}{r.badge && <RaceTypeBadge type={r.badge} />}</div>
      <div className="text-xs text-slate-500 mt-1">{r.detected}</div>
    </td>
    <td className="py-3 pr-4 num font-display text-xl font-bold text-amber-300 whitespace-nowrap" data-testid={`class-rule-score-${i}`}>{r.score}</td>
    <td className="py-3 text-xs text-slate-400 max-w-md">{r.notes}</td>
  </tr>
);

export default function ClassRules() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/admin/class-rules").then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e))); }, []);
  if (err) return <p className="text-rose-300 text-sm">{err}</p>;
  if (!d) return <div className="py-20 grid place-items-center"><div className="loader-bar" /></div>;
  return (
    <div className="space-y-8" data-testid="class-rules-page">
      <div>
        <div className="eyebrow mb-2 flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" />Admin • Class scoring</div>
        <h1 className="text-4xl sm:text-5xl font-bold">Class Rule Summary</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-3xl">Every special class rule in the order it is checked. The first rule that matches sets the class score, for both today's race and each horse's last start. Races that match none use the day / track table below.</p>
      </div>
      <div className="panel p-5 overflow-x-auto">
        <div className="eyebrow mb-3">Fixed class scores • first match wins</div>
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="pb-2 font-normal">#</th><th className="pb-2 font-normal">Rule & how it's spotted</th><th className="pb-2 font-normal">Score</th><th className="pb-2 font-normal">Notes</th>
          </tr></thead>
          <tbody>{d.fixed.map((r, i) => <FixedRow key={r.rule} r={r} i={i} />)}</tbody>
        </table>
      </div>
      <div className="panel p-5 overflow-x-auto">
        <div className="eyebrow mb-3">Everything else • class table by day and track</div>
        <table className="w-full text-sm min-w-[560px]">
          <tbody>{d.days.map((r, i) => (
            <tr key={r.when} className="border-t border-slate-800" data-testid={`class-day-rule-${i}`}>
              <td className="py-2.5 pr-4 text-slate-300">{r.when}</td>
              <td className="py-2.5 text-emerald-300 font-medium">{r.table}</td>
            </tr>
          ))}</tbody>
        </table>
        <p className="text-xs text-slate-500 mt-3">The score inside each table comes from the race's class or benchmark. Edit the tables in Rules & Matrices → Class Scores.</p>
      </div>
    </div>
  );
}
